import React, { useMemo, useState } from 'react';
import { Alert, Button, Snackbar } from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { useSelector } from 'react-redux';

import { RootState } from '../../store/types/sharedTypes';
import { GENERAL_FONT } from '../../constants/theme';
import {
  calculateAverageHeadache,
  getStatisticsByMonthsOrWeeks,
  sortDiaryEntriesByDate,
} from '../../lib/helpers/diary';
import { IDateRangeStatistics } from '../../lib/interfaces';
import { reportError } from '../../lib/sentry';

type Props = {
  disabled?: boolean;
};

const compareYearMonthDesc = (a: string, b: string) => {
  const [ay, am] = a.split('-').map(Number);
  const [by, bm] = b.split('-').map(Number);
  return by - ay || bm - am;
};

function buildMonthlyText(
  statsByMonths: Record<string, IDateRangeStatistics>,
  t: TFunction
) {
  const keys = Object.keys(statsByMonths ?? {}).sort(compareYearMonthDesc);
  if (!keys.length) return '';

  const lines: string[] = [];
  let totalMeds = 0;

  for (const key of keys.filter((k) => statsByMonths[k])) {
    const monthStats = statsByMonths[key];

    const label = dayjs(key, ['YYYY-M', 'YYYY-MM'], true).isValid()
      ? dayjs(key, ['YYYY-M', 'YYYY-MM']).format('MMMM YYYY')
      : key;

    lines.push(label.charAt(0).toUpperCase() + label.slice(1));

    const meds = Number(monthStats.numberOfLakemedels ?? 0);
    if (Number.isFinite(meds) && meds > 0) totalMeds += meds;

    const rows: Array<[string, number | undefined]> = [
      ['numberOfDaysAnswered', monthStats.numberOfDays],
      ['headache', monthStats.numberOfDaysWithHeadaches],
      ['migrain', monthStats.numberOfMigraines],
      ['tensionHeadache', monthStats.numberOfTensionHeadaches],
      ['horton', monthStats.numberOfHortons],
      ['otherHeadache', monthStats.numberOfOtherHeadaches],
      ['aura', monthStats.numberOfAuras],
      ['auraWithoutHeadache', monthStats.numberOfAurasWithoutHeadaches],
      ['noHeadache', monthStats.numberOfWithoutHeadaches],
      ['medication', monthStats.numberOfLakemedels],
      ['menstruation', monthStats.numberOfMens],
    ];

    const validRows = rows.filter(
      ([, v]) => typeof v === 'number' && v > 0
    ) as Array<[string, number]>;

    for (const [k, v] of validRows) lines.push(`${t(k)}: ${v}`);

    const avgDefs: Array<{
      labelKey: string;
      sumKey: keyof IDateRangeStatistics;
      countKey: keyof IDateRangeStatistics;
    }> = [
      {
        labelKey: 'vasMigraine',
        sumKey: 'totalSumOfIntensitiesMigraines',
        countKey: 'numberOfMigraines',
      },
      {
        labelKey: 'vasTensionHeadache',
        sumKey: 'totalSumOfIntensitiesTensionHeadaches',
        countKey: 'numberOfTensionHeadaches',
      },
      {
        labelKey: 'vasHorton',
        sumKey: 'totalSumOfIntensitiesHortons',
        countKey: 'numberOfHortons',
      },
      {
        labelKey: 'vasOtherHeadache',
        sumKey: 'totalSumOfIntensitiesOtherHeadaches',
        countKey: 'numberOfOtherHeadaches',
      },
    ];

    for (const { labelKey, sumKey, countKey } of avgDefs) {
      const count = Number(monthStats[countKey] ?? 0);
      const avg = calculateAverageHeadache(
        Number(monthStats[sumKey] ?? 0),
        count
      );
      if (count > 0) lines.push(`${t(labelKey)} ${t('average')}: ${avg}`);
    }

    lines.push('');
  }

  lines.push(t('treatmentEffect.medsTotalSentence', { count: totalMeds }));

  return lines.join('\n').trim();
}

export function useMonthlySummaryText(): string {
  const { t } = useTranslation();
  const { calendarStatistics } = useSelector((s: RootState) => s.general);

  return useMemo(() => {
    const entries = calendarStatistics?.statisticsEntries ?? [];
    if (!entries.length) return '';

    const sorted = sortDiaryEntriesByDate(entries);

    const statsByMonths = getStatisticsByMonthsOrWeeks(sorted, false);

    return buildMonthlyText(statsByMonths, t);
  }, [calendarStatistics?.statisticsEntries, t]);
}

const MonthlyCopySummaryButton: React.FC<Props> = ({ disabled }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const monthlyText = useMonthlySummaryText();
  const isDisabled = Boolean(disabled) || !monthlyText;

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        sx={{ textTransform: 'none', fontFamily: GENERAL_FONT }}
        disabled={isDisabled}
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(monthlyText);
          } catch (err) {
            reportError(err, { level: 'warning', tags: { area: 'clipboard' } });
            return;
          }
          setCopied(true);
        }}
      >
        {t('treatmentEffect.copyMonthlySummary')}
      </Button>

      <Snackbar
        open={copied}
        autoHideDuration={1500}
        onClose={() => setCopied(false)}
      >
        <Alert severity="success">{t('copiedSuccessfully')}</Alert>
      </Snackbar>
    </>
  );
};

export default MonthlyCopySummaryButton;
